import { all, call, put } from 'redux-saga/effects';
import { AxiosResponse } from 'axios';
import {
	convertCamelCaseIntoSnakeCase,
	getScaleRange,
	isObjectEmpty,
	makeRange,
	requestSagaFactory,
} from 'helpers';
import { IRequestParams, request } from 'helpers/request';
import { ENDPOINTS, logSagaErrorMessage } from 'utils';
import {
	IContentData,
	IDashboardTrendParams,
	IFiltersToReset,
	IRangeData,
	IResponseRound,
	IResultCommentsPayload,
	IResultFilterParams,
	IResultMapPayload,
	IResultMapPoint,
	IResultRole,
	IResultService,
	IResultServiceParam,
	IRoundParams,
	IStatisticParams,
	IStatisticPayload,
	IWithRoundsPayload,
	IWordcloudParams,
	SurveyRoundsData,
	GroupingType,
	IResponseServiceCards,
	IResponseServiceList,
	IPersonaData,
	IDashboardAction,
	IStatisticItem,
} from 'modules/admin-panel/pages/results/models';
import { StatisticGroupingTypes } from 'modules/admin-panel/models';

import { COMMENT_TYPES } from 'modules/admin-panel/utils';
import { prepareFilterPayload } from 'modules/admin-panel/pages/results/helpers';
import { getPrevRound } from 'modules/admin-panel/pages/results/helpers/getPrevRound';
import { setIsLocationChanged } from '../../map.actions';

export const endpointsFactory = ({
	getCommentsAction,
	getWordcloudAction,
	getServicesAction,
	getServiceStatsAction,
	getServiceCardsAction,
	getPointsAction,
	getRoundsAction,
	getTrendsAction,
	getCompareTrendsAction,
	getRolesAction,
	getBusinessUnitsAction,
	getRangeDataAction,
	getNpsResultsAction,
	getQuestionAnswersAction,
	getQuestionAnswersWordcloudAction,
	getStatisticAction,
	getRegionsAction,
}: any) => ({
	getCommentsSaga:
		getCommentsAction &&
		requestSagaFactory<IResultCommentsPayload, any>({
			actionApi: getCommentsAction,
			url: ENDPOINTS.resultDashboardComments,
			logErrorMessage: 'Get dashboard comments',
			parsePayload: (p) => prepareFilterPayload(p),
		}),
	getCommentsWordcloudSaga:
		getWordcloudAction &&
		requestSagaFactory<IWordcloudParams, any>({
			actionApi: getWordcloudAction,
			url: ENDPOINTS.resultDashboardWordcloud,
			logErrorMessage: 'Get dashboard wordcloud',
			parsePayload: (p) => prepareFilterPayload(p),
		}),
	getServiceStatsSaga:
		getServiceStatsAction &&
		requestSagaFactory<IResultFilterParams, any>({
			actionApi: getServiceStatsAction,
			url: ENDPOINTS.resultDashboardServiceStats,
			logErrorMessage: 'Get dashboard stats',
			parsePayload: (p) => prepareFilterPayload(p),
		}),
	getMapPointsSaga:
		getPointsAction &&
		(() => {
			const getMapDataSaga = requestSagaFactory<IResultMapPayload, any>({
				actionApi: getPointsAction,
				url: ENDPOINTS.resultDashboardPoints,
				logErrorMessage: 'Get dashboard map points',
			});

			return function* (
				{ payload }: { payload: IResultMapPayload },
				quiet?: boolean,
			) {
				const { prevSurveyRoundId } = payload;

				if (!quiet) {
					yield put(getPointsAction.loading());
				}

				try {
					const mapData = yield call(
						getMapDataSaga,
						{
							payload: {
								...payload,
								city: encodeURIComponent(payload.city || ''),
								office: encodeURIComponent(payload.office || ''),
							},
						},
						true,
					);
					const mapPrevData = prevSurveyRoundId
						? yield call(
								getMapDataSaga,
								{
									payload: {
										...payload,
										city: encodeURIComponent(payload.city || ''),
										office: encodeURIComponent(payload.office || ''),
										surveyRoundId: prevSurveyRoundId,
									},
								},
								true,
						  )
						: null;

					const resultData = {
						map: mapData,
						mapPrev: mapPrevData,
					};

					if (!quiet) {
						yield put(getPointsAction.success(resultData));
					}

					yield put(setIsLocationChanged(true));

					return resultData;
				} catch (e) {
					logSagaErrorMessage('Get dashboard map points', e);
					if (quiet) {
						throw e;
					} else {
						yield put(getPointsAction.fail(e));
					}
				}
			};
		})(),
	getTrendsSaga:
		getTrendsAction &&
		requestSagaFactory<IResultFilterParams, any>({
			actionApi: getTrendsAction,
			url: ENDPOINTS.resultDashboardTrends,
			logErrorMessage: 'Get dashboard trends',
			parsePayload: (p) => ({
				...p,
				city: encodeURIComponent(p.city || ''),
				office: encodeURIComponent(p.office || ''),
			}),
		}),
	getCompareTrendsSaga:
		getCompareTrendsAction &&
		requestSagaFactory<IResultFilterParams, any>({
			actionApi: getCompareTrendsAction,
			url: ENDPOINTS.resultDashboardTrends,
			logErrorMessage: 'Get dashboard compare trends',
			parsePayload: (p) => prepareFilterPayload(p),
		}),
	getRoundsSaga:
		getRoundsAction &&
		requestSagaFactory<IRoundParams, IResponseRound>({
			actionApi: getRoundsAction,
			url: ENDPOINTS.resultDashboardRounds,
			logErrorMessage: 'Get dashboard rounds',
			parsePayload: (p) => prepareFilterPayload(p),
		}),
	getServicesSaga:
		getServicesAction &&
		requestSagaFactory<IResultServiceParam, IResponseServiceList>({
			actionApi: getServicesAction,
			url: ENDPOINTS.resultDashboardServices,
			logErrorMessage: 'Get dashboard services',
		}),
	getServiceCardsSaga:
		getServiceCardsAction &&
		requestSagaFactory<IResultFilterParams, IResponseServiceCards>({
			actionApi: getServiceCardsAction,
			url: ENDPOINTS.resultDashboardServicesCards,
			logErrorMessage: 'get services cards',
			parsePayload: (p) => ({
				...p,
				city: encodeURIComponent(p.city || ''),
				office: encodeURIComponent(p.office || ''),
			}),
		}),
	getRolesSaga:
		getRolesAction &&
		requestSagaFactory<IResultFilterParams, any>({
			actionApi: getRolesAction,
			url: ENDPOINTS.resultDashboardRoles,
			logErrorMessage: 'Get dashboard roles',
			parsePayload: (p) => ({
				...p,
				city: encodeURIComponent(p.city || ''),
				office: encodeURIComponent(p.office || ''),
			}),
		}),
	getBusinessUnitsSaga:
		getBusinessUnitsAction &&
		requestSagaFactory<IResultFilterParams, any>({
			actionApi: getBusinessUnitsAction,
			url: ENDPOINTS.resultDashboardBusinessUnits,
			logErrorMessage: 'Get dashboard business units',
			parsePayload: (p) => prepareFilterPayload(p),
		}),
	getRangeDataSaga:
		getRangeDataAction &&
		requestSagaFactory<IResultFilterParams, any>({
			actionApi: getRangeDataAction,
			url: ENDPOINTS.resultDashboardRangeData,
			logErrorMessage: 'Get dashboard range data',
		}),
	getNpsResultsSaga:
		getNpsResultsAction &&
		requestSagaFactory<IResultFilterParams, any>({
			actionApi: getNpsResultsAction,
			url: ENDPOINTS.resultDashboardNPSTrends,
			logErrorMessage: 'Get dashboard nps results',
			parsePayload: (p) => prepareFilterPayload(p),
		}),
	getQuickQuestionAnswersSaga:
		getQuestionAnswersAction &&
		requestSagaFactory<IResultFilterParams, any>({
			actionApi: getQuestionAnswersAction,
			url: ENDPOINTS.resultDashboardQuestionAnswers,
			logErrorMessage: 'Get dashboard quick question answers',
			parsePayload: (p) => prepareFilterPayload(p),
		}),
	getQuickQuestionAnswersWordcloudSaga:
		getQuestionAnswersWordcloudAction &&
		requestSagaFactory<IResultFilterParams, any>({
			actionApi: getQuestionAnswersWordcloudAction,
			url: ENDPOINTS.resultDashboardQuestionAnswersWordcloud,
			logErrorMessage: 'Get dashboard quick question answers wordcloud',
			parsePayload: (p) => prepareFilterPayload(p),
		}),
	getStatisticSaga:
		getStatisticAction &&
		(() => {
			return function* ({ payload }: { payload: IStatisticParams }) {
				yield put(getStatisticAction.loading());

				const { groupingType } = payload;
				const type = convertCamelCaseIntoSnakeCase(
					groupingType || '',
				) as GroupingType;

				const requestPayload: IStatisticPayload = {
					...payload,
					city: encodeURIComponent(payload.city || ''),
					office: encodeURIComponent(payload.office || ''),
					businessUnit: encodeURIComponent(payload.businessUnit || ''),
					groupingType: type || StatisticGroupingTypes.location,
				};
				const getStatisticDataParams: IRequestParams = {
					url: ENDPOINTS.locationStatistic,
					params: requestPayload,
				};

				try {
					const { data }: AxiosResponse<IStatisticItem[]> = yield call(
						request,
						getStatisticDataParams,
					);

					yield put(
						getStatisticAction.success({
							statistic: data,
							statisticGroupingType: requestPayload.groupingType,
						}),
					);

					return data;
				} catch (e) {
					logSagaErrorMessage('Get dashboard statistic', e);
					yield put(getStatisticAction.fail(e));
				}
			};
		})(),
	getRegionsSaga:
		getRegionsAction &&
		requestSagaFactory<IDashboardTrendParams, any>({
			actionApi: getRegionsAction,
			url: ENDPOINTS.regions,
			logErrorMessage: 'Get dashboard regions',
		}),
});

export const getAndSetRolesSagaFactory = ({
	getRolesSaga,
	setActiveRoleAction,
}: any) =>
	function* ({
		payload: {
			surveyId,
			roundId,
			roleId,
			serviceId,
			businessUnit,
			region,
			country,
			city,
			office,
		},
	}: {
		type: string;
		payload: IWithRoundsPayload;
	}) {
		const encodedBusinessUnit = encodeURIComponent(businessUnit || '');

		try {
			const roles = yield call(getRolesSaga, {
				payload: {
					surveyRoundId: roundId as string,
					country,
					city,
					office,
					businessUnit: encodedBusinessUnit,
					region,
				},
			});

			const newActiveRoleId: number =
				roles?.find((role: IResultRole) => role.id === roleId)?.id ||
				roles?.[0].id;

			if (roleId && newActiveRoleId && roleId !== newActiveRoleId) {
				yield put(
					setActiveRoleAction({
						surveyId,
						roundId,
						serviceId,
						roleId: newActiveRoleId,
						country,
						city,
						office,
						businessUnit: encodedBusinessUnit,
						region,
					}),
				);

				return true;
			}

			return false;
		} catch (e) {
			logSagaErrorMessage('Get dashboard roles', e);
		}
	};

export const getRolesAndContentDataSagaFactory = ({
	getAndSetRolesSaga,
	getContentDataSaga,
	getContentAction,
}: any) =>
	function* (action: { type: string; payload: IWithRoundsPayload }) {
		yield put(getContentAction.loading());
		if (getAndSetRolesSaga) {
			const setNewRole = yield call(getAndSetRolesSaga, action);
			if (setNewRole) return;
		}
		yield call(getContentDataSaga, action);
	};

export const getContentDataSagaFactory = ({
	getTrendsSaga,
	getMapPointsSaga,
	getRegionsSaga,
	getBusinessUnitsSaga,
	getServiceCardsSaga,
	getServiceStatsSaga,
	setActiveCommentTypeAction,
	setActiveQuickAnswersAction,
	getContentAction,
}: any) =>
	function* (action: IDashboardAction, quiet?: boolean) {
		const {
			serviceId,
			roleId,
			country,
			city,
			office,
			roundId,
			rounds,
			surveyId,
			businessUnit,
			region,
			isServicePage,
		} = action.payload;
		const encodedBusinessUnit = encodeURIComponent(businessUnit || '');

		const prevRound = getPrevRound(rounds, roundId);
		const callDataStack = [];
		let sagaCounter = 0;
		const sagasMap = {
			map: -1,
			regions: -1,
			businessUnits: -1,
			trends: -1,
			compareTrends: -1,
			servicesCards: -1,
			serviceStats: -1,
		};

		if (!quiet) yield put(getContentAction.loading());

		if (getMapPointsSaga) {
			callDataStack.push(
				call(
					getMapPointsSaga,
					{
						payload: {
							surveyRoundId: roundId,
							prevSurveyRoundId: prevRound?.surveyRoundId,
							country,
							city,
							office,
							roleId,
							serviceId,
							businessUnit: encodedBusinessUnit,
							region,
						},
					},
					quiet,
				),
			);
			sagasMap.map = sagaCounter;
			sagaCounter += 1;
		}

		if (getRegionsSaga) {
			callDataStack.push(
				call(
					getRegionsSaga,
					{
						payload: {
							surveyRoundId: roundId,
							prevSurveyRoundId: prevRound?.surveyRoundId,
							roleId,
							serviceId,
							businessUnit: encodedBusinessUnit,
						},
					},
					quiet,
				),
			);
			sagasMap.regions = sagaCounter;
			sagaCounter += 1;
		}

		if (getBusinessUnitsSaga) {
			callDataStack.push(
				call(
					getBusinessUnitsSaga,
					{
						payload: {
							surveyRoundId: roundId,
							region,
							country,
							city,
							office,
							roleId,
						},
					},
					quiet,
				),
			);
			sagasMap.businessUnits = sagaCounter;
			sagaCounter += 1;
		}

		if (getTrendsSaga) {
			callDataStack.push(
				call(
					getTrendsSaga,
					{
						payload: {
							surveyId,
							country,
							city,
							office,
							roleId,
							serviceId,
							businessUnit: encodedBusinessUnit,
							region,
						},
					},
					quiet,
				),
			);
			sagasMap.trends = sagaCounter;
			sagaCounter += 1;
		}

		if (getServiceCardsSaga) {
			callDataStack.push(
				call(
					getServiceCardsSaga,
					{
						payload: {
							surveyId,
							surveyRoundId: roundId,
							region,
							country,
							city,
							office,
							roleId,
							businessUnit: encodedBusinessUnit,
						},
					},
					quiet,
				),
			);
			sagasMap.servicesCards = sagaCounter;
			sagaCounter += 1;
		}

		if (getServiceStatsSaga) {
			callDataStack.push(
				call(
					getServiceStatsSaga,
					{
						payload: {
							surveyRoundId: roundId,
							country,
							city,
							office,
							roleId,
							serviceId,
							businessUnit: encodedBusinessUnit,
							region,
						},
					},
					quiet,
				),
			);
			sagasMap.serviceStats = sagaCounter;
			sagaCounter += 1;
		}

		const contentData = yield all(callDataStack);
		if (setActiveQuickAnswersAction && !isServicePage) {
			yield put(setActiveQuickAnswersAction(COMMENT_TYPES.all));
		}
		if (!quiet) {
			yield put(setActiveCommentTypeAction(COMMENT_TYPES.all));
			yield put(getContentAction.success());
		}

		return {
			trends: contentData[sagasMap.trends],
			map: contentData[sagasMap.map],
			regions: contentData[sagasMap.regions],
			businessUnits: contentData[sagasMap.businessUnits],
			servicesCards: contentData[sagasMap.servicesCards],
			serviceStats: contentData[sagasMap.serviceStats],
		};
	};

export const getAllDataSagaFactory = ({
	getRoundsSaga,
	getServicesSaga,
	getRolesSaga,
	getStatisticSaga,
	getContentDataSaga,
	getPersonaListSaga,
	getAllDataAction,
	getRangeDataSaga,
}: any) =>
	function* getAllDashboardDataSaga({ payload }: IDashboardAction) {
		const {
			surveyId,
			roundId,
			serviceId,
			roleId,
			country,
			city,
			office,
			businessUnit,
			region,
			groupingType,
			isServicePage,
		} = payload;
		const encodedBusinessUnit = encodeURIComponent(businessUnit || '');

		yield put(getAllDataAction.loading());
		try {
			const roundsData: SurveyRoundsData = yield call(
				getRoundsSaga,
				{ payload: { surveyId } },
				true,
			);
			const { message, surveyList } = roundsData;
			const { name, surveyRoundList: rounds, quick } = surveyList[0] || {};
			if (surveyList.length === 0 && message) {
				logSagaErrorMessage(
					'Set dashboard active role or service',
					new Error('empty rounds'),
				);
				yield put(
					getAllDataAction.success({
						rounds: [],
						roundsErrorMessage: message,
					}),
				);
				return;
			}
			const activeRound = roundId
				? rounds.find((round) => round.surveyRoundId === roundId)
				: rounds[rounds.length - 1];

			const services: IResultService[] = yield call(
				getServicesSaga,
				{ payload: { surveyRoundId: activeRound?.surveyRoundId } },
				true,
			);

			const activeService = serviceId
				? services.find((service) => service.id === serviceId)
				: services[0];

			let marks;
			if (getRangeDataSaga) {
				const data: IRangeData = yield call(getRangeDataSaga, {
					payload: { surveyRoundId: activeRound?.surveyRoundId },
				});

				marks = makeRange(data.scalePoints);
				yield put(
					getAllDataAction.success({
						scalePoints: data.scalePoints,
						scaleRange: getScaleRange(data),
					}),
				);
			}

			const [
				roles,
				{ trends, map, regions, businessUnits, servicesCards, serviceStats },
			]: [IResultRole, IContentData] = yield all([
				call(
					getRolesSaga,
					{
						payload: {
							surveyRoundId: activeRound?.surveyRoundId,
							country,
							city,
							office,
							businessUnit: encodedBusinessUnit,
							region,
						},
					},
					true,
				),
				call(
					getContentDataSaga,
					{
						payload: {
							surveyId,
							rounds,
							roundId: activeRound?.surveyRoundId,
							serviceId: activeService?.id,
							marks,
							roleId,
							region,
							country,
							city,
							office,
							businessUnit: encodedBusinessUnit,
							isSurveyQuick: quick,
							isServicePage,
						},
					},
					true,
				),
			]);

			let personaList: IPersonaData[] | undefined;
			if (!isServicePage) {
				personaList = yield call(getPersonaListSaga, {
					payload: {
						surveyRoundId: activeRound?.surveyRoundId || roundId,
					},
				});
			}

			if (servicesCards) {
				const {
					firstPerformanceSliderName,
					secondPerformanceSliderName,
					thirdPerformanceSliderName,
				} = servicesCards;
				yield put(
					getAllDataAction.success({
						servicesCards: servicesCards?.serviceTrends,
						performanceSlidersNames: {
							firstPerformanceSliderName,
							secondPerformanceSliderName,
							thirdPerformanceSliderName,
						},
					}),
				);
			}

			yield put(
				getAllDataAction.success({
					rounds,
					activeRound: activeRound?.surveyRoundId,
					services,
					activeService: activeService?.id,
					roles,
					businessUnits,
					trends,
					serviceStats,
					surveyName: name,
					personaList,
					isSurveyQuick: quick,
					activeRole: roleId,
					...map,
					regions,
				}),
			);

			yield call(getStatisticSaga, {
				payload: {
					surveyRoundId: activeRound?.surveyRoundId,
					roleId,
					serviceId: activeService?.id,
					businessUnit: encodedBusinessUnit,
					region,
					country,
					city,
					office,
					groupingType,
					isServicePage,
				},
			});
		} catch (e) {
			logSagaErrorMessage('Get all data', e);
			yield put(getAllDataAction.fail(e));
		}
	};

export const getAllDataForRoundSagaFactory = ({
	getServicesSaga,
	getRegionsSaga,
	getMapPointsSaga,
	getRolesSaga,
	getBusinessUnitsSaga,
	getStatisticSaga,
	getPersonaListSaga,
	getRoundDataSaga,
	getRoundDataAction,
}: any) =>
	function* getAllDashboardDataSaga({ payload }: IDashboardAction) {
		const {
			serviceId,
			roleId,
			country,
			city,
			office,
			roundId,
			rounds,
			surveyId,
			businessUnit,
			region,
			groupingType,
			isSurveyQuick,
			isServicePage,
		} = payload;

		const filtersToReset = {} as IFiltersToReset;
		let newRegion = region;
		let newCountry = country;
		let newCity = city;
		let newOffice = office;
		let newBU = businessUnit;
		let newRole = roleId;
		let newGroupingType = groupingType;

		yield put(getRoundDataAction.loading());

		try {
			if (region) {
				// check if region exists
				const regions: string[] = yield call(
					getRegionsSaga,
					{
						payload: {
							surveyRoundId: roundId,
							serviceId,
						},
					},
					true,
				);

				if (!regions.includes(region)) {
					newRegion = undefined;
					filtersToReset.region = undefined;
				}
			}

			if (country) {
				// check if location (country, city, office) exists
				const {
					map: pointsWithLocationFilters,
				}: { map: IResultMapPoint[] } = yield call(
					getMapPointsSaga,
					{
						payload: {
							surveyRoundId: roundId,
							region: newRegion,
							country,
							city,
							office,
							serviceId,
						},
					},
					true,
				);

				// if the full location (country, city, office) doesn't exist
				if (!pointsWithLocationFilters.length) {
					if (office) {
						filtersToReset.office = undefined;
					}
					newOffice = undefined;

					if (!city) {
						newCountry = undefined;
						filtersToReset.country = undefined;
					} else {
						const {
							map: pointsForRegion,
						}: { map: IResultMapPoint[] } = yield call(
							getMapPointsSaga,
							{
								payload: {
									surveyRoundId: roundId,
									region: newRegion,
									serviceId,
								},
							},
							true,
						);

						const isCity = !!pointsForRegion.find((p) => {
							return p.country === country && p.city === city ? p : undefined;
						});

						if (!isCity) {
							newCity = undefined;
							filtersToReset.city = undefined;

							const isCountry = !!pointsForRegion.find((p) => {
								return p.country === country ? p : undefined;
							});

							if (!isCountry) {
								newCountry = undefined;
								filtersToReset.country = undefined;
							}
						}
					}
				}
			}

			// check if data for all other filters (businessUnit, role) exists
			if (businessUnit || roleId) {
				const {
					map: pointsWithAllFilters,
				}: { map: IResultMapPoint[] } = yield call(
					getMapPointsSaga,
					{
						payload: {
							surveyRoundId: roundId,
							serviceId,
							region: newRegion,
							country: newCountry,
							city: newCity,
							office: newOffice,
							roleId,
							businessUnit: encodeURIComponent(businessUnit || ''),
						},
					},
					true,
				);

				// if there is no data for businessUnit so reset it
				if (!pointsWithAllFilters.length) {
					if (!businessUnit) {
						newRole = undefined;
						filtersToReset.roleId = undefined;
					} else if (!newRole) {
						newBU = undefined;
						filtersToReset.businessUnit = undefined;
					} else {
						newBU = undefined;
						filtersToReset.businessUnit = undefined;

						const {
							map: pointsWithoutBU,
						}: { map: IResultMapPoint[] } = yield call(
							getMapPointsSaga,
							{
								payload: {
									surveyRoundId: roundId,
									serviceId,
									region: newRegion,
									country: newCountry,
									city: newCity,
									office: newOffice,
									roleId,
								},
							},
							true,
						);

						// if there is no data for roleId reset it
						if (!pointsWithoutBU.length) {
							newRole = undefined;
							filtersToReset.roleId = undefined;
						}
					}
				}
			}

			if (groupingType === StatisticGroupingTypes.businessUnit) {
				const units: string[] = yield call(getBusinessUnitsSaga, {
					payload: {
						surveyRoundId: roundId,
						serviceId,
					},
				});
				if (!units.length) {
					newGroupingType = StatisticGroupingTypes.location;
					filtersToReset.statisticGroupingType = undefined;
				}
			}

			const [
				services,
				roles,
				{ trends, map, regions, serviceStats, businessUnits, servicesCards },
			]: [IResultService[], IResultRole, IContentData] = yield all([
				call(getServicesSaga, {
					payload: {
						surveyRoundId: roundId,
					},
				}),
				call(
					getRolesSaga,
					{
						payload: {
							surveyRoundId: roundId,
							region: newRegion,
							country: newCountry,
							city: newCity,
							office: newOffice,
							businessUnit: newBU,
						},
					},
					true,
				),
				call(
					getRoundDataSaga,
					{
						payload: {
							surveyId,
							rounds,
							roundId,
							serviceId,
							region: newRegion,
							country: newCountry,
							city: newCity,
							office: newOffice,
							roleId: newRole,
							businessUnit: newBU,
							isSurveyQuick,
						},
					},
					true,
				),
			]);

			yield call(getStatisticSaga, {
				payload: {
					surveyRoundId: roundId,
					roleId: newRole,
					businessUnit: newBU,
					region: newRegion,
					country: newCountry,
					city: newCity,
					office: newOffice,
					groupingType: newGroupingType,
					isServicePage,
				},
			});

			let personaList: IPersonaData[] | undefined;
			if (!isServicePage) {
				personaList = yield call(getPersonaListSaga, {
					payload: {
						surveyRoundId: roundId,
					},
				});
			}

			if (servicesCards) {
				const {
					firstPerformanceSliderName,
					secondPerformanceSliderName,
					thirdPerformanceSliderName,
				} = servicesCards;
				yield put(
					getRoundDataAction.success({
						servicesCards: servicesCards?.serviceTrends,
						performanceSlidersNames: {
							firstPerformanceSliderName,
							secondPerformanceSliderName,
							thirdPerformanceSliderName,
						},
					}),
				);
			}

			yield put(
				getRoundDataAction.success({
					services,
					roles,
					trends,
					rounds,
					regions,
					...map,
					statisticGroupingType: newGroupingType,
					serviceStats,
					businessUnits,
					personaList,

					filtersToReset: isObjectEmpty(filtersToReset)
						? undefined
						: filtersToReset,
				}),
			);
		} catch (e) {
			logSagaErrorMessage('Get all data after round change', e);
			yield put(getRoundDataAction.fail(e));
		}
	};

export const getDataWithGridStatisticFactory = ({
	getDataSaga,
	getStatisticSaga,
}: {
	getDataSaga: (p: IDashboardAction) => any;
	getStatisticSaga: (p: IDashboardAction) => void;
}) =>
	function* (action: IDashboardAction) {
		const {
			payload: {
				roundId,
				serviceId,
				roleId,
				businessUnit,
				region,
				country,
				city,
				office,
				groupingType,
				isServicePage,
			},
		} = action;

		const statisticPayload = {
			surveyRoundId: roundId,
			serviceId,
			roleId,
			businessUnit,
			region,
			country,
			city,
			office,
			groupingType,
			isServicePage,
		};

		yield all([
			call(getDataSaga, action),
			call(getStatisticSaga, { ...action, payload: statisticPayload }),
		]);
	};
